import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from 'aws-blocks';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import { PLAYER_SKINS } from '../../player-colors';
import type { StoreItem } from './StoreItemTile';

export type StoreCategory =
  | 'gems'
  | 'skins'
  | 'icons'
  | 'emotes'
  | 'boards';

export const STORE_CATEGORIES: Array<{
  id: StoreCategory;
  label: string;
  path: string;
  blurb: string;
}> = [
  {
    id: 'gems',
    label: 'Gems',
    path: '/store/gems',
    blurb: 'Hard currency packs via Stripe',
  },
  {
    id: 'skins',
    label: 'Skins',
    path: '/store/skins',
    blurb: 'Army looks for the battlefield',
  },
  {
    id: 'icons',
    label: 'Icons',
    path: '/store/icons',
    blurb: 'Profile portraits for nav and lobby',
  },
  {
    id: 'emotes',
    label: 'Emotes',
    path: '/store/emotes',
    blurb: 'Match reactions and profile flair',
  },
  {
    id: 'boards',
    label: 'Boards',
    path: '/store/boards',
    blurb: 'Battlefield themes — coming soon',
  },
];

export const BOARD_PLACEHOLDERS: StoreItem[] = [
  {
    itemId: 'board-marble',
    title: 'Marble Court',
    subTitle: 'Board skin',
    description: 'Polished marble tiles for your battlefield.',
    category: 'boards',
    subCategory: 'skins',
    rarity: 'uncommon',
    backgroundColor: '#94a3b8',
    comingSoon: true,
  },
  {
    itemId: 'board-obsidian',
    title: 'Obsidian Grid',
    subTitle: 'Board skin',
    description: 'Dark volcanic glass with gold inlays.',
    category: 'boards',
    subCategory: 'skins',
    rarity: 'rare',
    backgroundColor: '#1e293b',
    comingSoon: true,
  },
  {
    itemId: 'board-aurora',
    title: 'Aurora Field',
    subTitle: 'Board skin',
    description: 'Shifting northern lights across the board.',
    category: 'boards',
    subCategory: 'skins',
    rarity: 'legendary',
    backgroundColor: '#0f766e',
    comingSoon: true,
  },
];

function defaultSkinStoreItems(): StoreItem[] {
  return PLAYER_SKINS.filter((c) => c.isDefault).map((c) => ({
    itemId: c.id,
    colorId: c.id,
    skinId: c.id,
    title: c.label,
    subTitle: 'Default army skin',
    description: `${c.label} is free and always available.`,
    category: 'skins',
    subCategory: 'army',
    rarity: c.rarity ?? 'common',
    fill: c.fill,
    stroke: c.stroke,
    backgroundColor: c.fill,
    coins: 0,
    gems: 0,
    comingSoon: false,
  }));
}

export function isSkinCategory(category?: string) {
  return category === 'skins' || category === 'colors';
}

export function rarityRank(rarity?: string) {
  const rank: Record<string, number> = {
    legendary: 0,
    rare: 1,
    uncommon: 2,
    common: 3,
  };
  return rank[String(rarity ?? 'common').toLowerCase()] ?? 9;
}

export const RARITY_ORDER = [
  'legendary',
  'rare',
  'uncommon',
  'common',
] as const;

export type OwnershipFilter = 'all' | 'owned' | 'not_owned';

export function useStoreCatalog() {
  const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { addSnackBar } = useSnackBarContext();

  const reload = useCallback(() => {
    setLoading(true);
    void api
      .listStoreItems()
      .then((items: StoreItem[]) => setStoreItems(items))
      .catch(() => {
        addSnackBar({
          title: 'ERROR',
          message: 'Could not load store items.',
          variant: 'danger',
          timeout: 2500,
        });
      })
      .finally(() => setLoading(false));
  }, [addSnackBar]);

  useEffect(() => {
    reload();
  }, [reload]);

  const allItems = useMemo(() => {
    const normalized = storeItems
      .map((item) =>
        item.category === 'colors'
          ? {
              ...item,
              category: 'skins',
              subCategory:
                item.subCategory === 'team' ? 'army' : item.subCategory,
            }
          : item
      )
      .filter((i) => String(i.category) !== 'boosts');
    const skinIds = new Set(
      normalized
        .filter((i) => isSkinCategory(String(i.category)))
        .map((i) => String(i.itemId))
    );
    const defaults = defaultSkinStoreItems().filter(
      (i) => !skinIds.has(String(i.itemId))
    );
    return [...defaults, ...normalized];
  }, [storeItems]);

  const byCategory = useMemo(() => {
    const gems = allItems
      .filter((i) => i.category === 'gems')
      .sort((a, b) => Number(a.money ?? 0) - Number(b.money ?? 0));
    const skins = allItems
      .filter((i) => isSkinCategory(String(i.category)))
      .sort(
        (a, b) =>
          rarityRank(String(a.rarity)) - rarityRank(String(b.rarity)) ||
          String(a.title).localeCompare(String(b.title))
      );
    const icons = allItems
      .filter((i) => i.category === 'icons')
      .sort(
        (a, b) =>
          rarityRank(String(a.rarity)) - rarityRank(String(b.rarity)) ||
          String(a.title).localeCompare(String(b.title))
      );
    const emotes = allItems
      .filter((i) => i.category === 'emotes')
      .sort(
        (a, b) =>
          rarityRank(String(a.rarity)) - rarityRank(String(b.rarity)) ||
          String(a.title).localeCompare(String(b.title))
      );
    return {
      gems,
      skins,
      icons,
      emotes,
      boards: BOARD_PLACEHOLDERS,
    } satisfies Record<StoreCategory, StoreItem[]>;
  }, [allItems]);

  const featured = useMemo(() => {
    const gemHighlight = byCategory.gems[1] ?? byCategory.gems[0];
    const cosmetics = [...byCategory.icons, ...byCategory.emotes]
      .filter((i) => !i.comingSoon && Number(i.priceGems ?? 0) > 0)
      .slice(0, 2);
    return { gemHighlight, cosmetics };
  }, [byCategory]);

  return { allItems, byCategory, featured, loading, reload };
}

export function filterStoreItems(
  items: StoreItem[],
  opts: {
    query: string;
    rarity: string; // 'all' | rarity
    ownership: OwnershipFilter;
    legacySeasonId: string; // 'all' | 'none' | seasonId
    isOwned: (item: StoreItem) => boolean;
  }
) {
  const q = opts.query.trim().toLowerCase();
  return items.filter((item) => {
    if (opts.rarity !== 'all') {
      if (String(item.rarity ?? 'common').toLowerCase() !== opts.rarity) {
        return false;
      }
    }
    if (opts.legacySeasonId === 'none') {
      if (item.subCategory === 'legacy' || item.legacySeasonId) return false;
    } else if (opts.legacySeasonId !== 'all') {
      if (String(item.legacySeasonId ?? '') !== opts.legacySeasonId) {
        return false;
      }
    }
    const owned = opts.isOwned(item);
    if (opts.ownership === 'owned' && !owned) return false;
    if (opts.ownership === 'not_owned' && owned) return false;
    if (q) {
      const hay = `${item.title ?? ''} ${item.subTitle ?? ''} ${item.description ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function groupByRarity(items: StoreItem[]) {
  const groups: Array<{ rarity: string; items: StoreItem[] }> = [];
  for (const rarity of RARITY_ORDER) {
    const bucket = items.filter(
      (i) => String(i.rarity ?? 'common').toLowerCase() === rarity
    );
    if (bucket.length) groups.push({ rarity, items: bucket });
  }
  const leftover = items.filter(
    (i) =>
      !RARITY_ORDER.includes(
        String(i.rarity ?? 'common').toLowerCase() as (typeof RARITY_ORDER)[number]
      )
  );
  if (leftover.length) groups.push({ rarity: 'other', items: leftover });
  return groups;
}
